import { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { safeGetItem, safeSetItem } from '../../utils/localStorage';

const STORAGE_KEY = 'theme-preference';
const THEME_COLOR_LIGHT = '#58CC02';
const THEME_COLOR_DARK = '#1c1410';

const ThemeContext = createContext(null);

function getSystemTheme() {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyTheme(resolved) {
  document.documentElement.setAttribute('data-theme', resolved);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    meta.setAttribute('content', resolved === 'dark' ? THEME_COLOR_DARK : THEME_COLOR_LIGHT);
  }
}

export function ThemeProvider({ children }) {
  const [preference, setPreference] = useState(() => {
    const stored = safeGetItem(STORAGE_KEY, 'system');
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
    return 'system';
  });

  const resolvedTheme = preference === 'system' ? getSystemTheme() : preference;
  const isDark = resolvedTheme === 'dark';

  // Apply theme to DOM whenever resolved theme changes
  useEffect(() => {
    applyTheme(resolvedTheme);
  }, [resolvedTheme]);

  // Listen for OS dark mode changes when in system mode
  useEffect(() => {
    if (preference !== 'system') return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => applyTheme(getSystemTheme());
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [preference]);

  const setThemePreference = useCallback((pref) => {
    setPreference(pref);
    safeSetItem(STORAGE_KEY, pref);
  }, []);

  const value = useMemo(() => ({
    preference,
    resolvedTheme,
    isDark,
    setThemePreference,
  }), [preference, resolvedTheme, isDark, setThemePreference]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}

export function useForceLightTheme() {
  useEffect(() => {
    const prev = document.documentElement.getAttribute('data-theme');
    document.documentElement.setAttribute('data-theme', 'light');
    return () => {
      if (prev) document.documentElement.setAttribute('data-theme', prev);
      else document.documentElement.removeAttribute('data-theme');
    };
  }, []);
}
